import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, SimpleChanges, ViewChild } from '@angular/core';
import anime from 'animejs/lib/anime.es.js';

@Component({
  selector: 'app-ai-blob',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './ai-blob.component.html',
  styleUrl: './ai-blob.component.scss'
})
export class AiBlobComponent {
  @Input() audioStream?: MediaStream;
  @Input() isAnimated: boolean = false;
  @Input() isUser: boolean = false;
  @ViewChild('svgElement', { static: true }) svgElement!: ElementRef<SVGSVGElement>;
  @ViewChild('circle') circle!: ElementRef<HTMLDivElement>;

  private audioContext?: AudioContext;
  private analyser?: AnalyserNode;
  private dataArray?: Uint8Array;
  private animationFrameId?: number;
  private animationTimeout?: number;
  private colorScheme: any[];
  private dots: SVGCircleElement[] = [];

  private centerX = 250;
  private centerY = 250;
  private maxRadius = 220;
  private numDots = 600;
  private amplitude = 25;
  private frequency = 30;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isUser']) {
      this.updateColorScheme();
    }

    if (changes['audioStream'] && this.audioStream) {
      this.startAudioProcessing();
    }

    if (changes['isAnimated']) {
      if (this.isAnimated) {
        this.animateSwarm();
      }
    }
  }

  ngAfterViewInit(): void {
    this.updateColorScheme();
    if (!this.isUser) {
      this.simulateAudioAnimation();
    }
  }

  private updateColorScheme() {
    this.colorScheme = this.isUser
      ? ["#FF3B30", "#FF5E57", "#FF6B81", "#FF2D55", "#D6001C", "#B30000"]
      : ["#60AFFF", "#4F90FF", "#3A72FF", "#2A5CDC", "#1C47B7", "#12358F"];

    this.generateDots();
  }

  startAudioProcessing() {
    this.audioContext = new AudioContext();
    const source = this.audioContext.createMediaStreamSource(this.audioStream);

    this.analyser = this.audioContext.createAnalyser();
    this.analyser.fftSize = 32;
    const bufferLength = this.analyser.frequencyBinCount;
    this.dataArray = new Uint8Array(bufferLength);

    source.connect(this.analyser);
    this.animateCircle();
  }

  animateCircle() {
    if (!this.analyser || !this.dataArray) return;

    this.analyser.getByteFrequencyData(this.dataArray);
    const volume = this.dataArray.reduce((a, b) => a + b) / this.dataArray.length;
    const scale = 1 + (volume / 255) * 0.5;
    const rotation = (volume / 255) * 360;

    if (this.circle) {
      this.circle.nativeElement.style.transform = `scale(${scale}) rotate(${rotation}deg)`;
    }

    this.animationFrameId = requestAnimationFrame(() => this.animateCircle());
  }

  simulateAudioAnimation() {
    const minScale = 1;
    const maxScale = 1.2;
    let currentRotation = 0;

    const animate = () => {
      const randomScale = minScale + Math.random() * (maxScale - minScale);
      const randomDuration = 200 + Math.random() * 200; // 200-400ms
      const rotationIncrement = 90 + Math.random() * 270;
      currentRotation = (currentRotation + rotationIncrement) % 360;

      if (this.circle) {
        this.circle.nativeElement.style.transition = `transform ${randomDuration}ms ease-in-out`;
        this.circle.nativeElement.style.transform = `scale(${randomScale}) rotate(${currentRotation}deg)`;
      }

      this.animationTimeout = window.setTimeout(() => requestAnimationFrame(animate), randomDuration);
    };

    animate();
  }

  private generateDots(): void {
    const svg = this.svgElement.nativeElement;
    svg.innerHTML = '';
    this.dots = [];

    const sizes = [8, 14, 24];

    for (let i = 0; i < this.numDots; i++) {
      const angle = Math.random() * 2 * Math.PI;
      const r = this.maxRadius * Math.sqrt(Math.random());
      const cx = this.centerX + r * Math.cos(angle);
      const cy = this.centerY + r * Math.sin(angle);

      const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
      circle.setAttribute('cx', cx.toString());
      circle.setAttribute('cy', cy.toString());
      circle.setAttribute('r', sizes[Math.floor(Math.random() * sizes.length)].toString());
      circle.setAttribute('fill', this.colorScheme[Math.floor(Math.random() * this.colorScheme.length)]);
      
      circle.dataset.baseCx = cx.toString();
      circle.dataset.baseCy = cy.toString();
      circle.dataset.angle = angle.toString();
      circle.dataset.baseR = r.toString();

      svg.appendChild(circle);
      this.dots.push(circle);
    }
  }

  private animateSwarm(): void {
    anime({
      targets: { t: 0 },
      t: 2 * Math.PI,
      duration: 800,
      easing: 'linear',
      loop: true,
      update: anim => {
        const t = anim.animations[0].currentValue;
        this.dots.forEach(dot => {
          const baseR = parseFloat(dot.dataset.baseR || '0');
          const angle = parseFloat(dot.dataset.angle || '0');
          const oscillation = this.amplitude * Math.sin(t + angle * this.frequency);
          const newR = baseR + oscillation;
          dot.setAttribute('cx', (this.centerX + newR * Math.cos(angle)).toString());
          dot.setAttribute('cy', (this.centerY + newR * Math.sin(angle)).toString());
        });
      }
    });

    anime({
      targets: '.circle-one',
      duration: 4000,
      direction: 'alternate',
      easing: 'easeInOutSine',
      loop: true,
    });
  }

  ngOnDestroy() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    if (this.animationTimeout) {
      clearTimeout(this.animationTimeout);
    }
    if (this.audioContext) {
      this.audioContext.close();
    }
    this.audioStream = null;
    this.dots = [];
  }
}